import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  applyImportFavorites,
  enrichFavoritesWithApi,
  ParsedFavoriteHymn,
  parseFavoritesText,
} from "@/utils/import-favorites";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  ClipboardPaste,
  FileDown,
  FolderInput,
  HelpCircle,
  Loader2,
  Music,
  Plus,
  RefreshCw,
  Trash2,
  Volume2,
} from "lucide-react";
import React, { useEffect, useId, useState } from "react";
import { toast } from "sonner";

interface ImportFavoritesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onImportSuccess?: () => void;
}

const SAMPLE_EXPORT_TEXT = `- [CE-CR - Chants d'Espérance (Kreyòl)] N° 54 - Leve, nanm mwen, ak solèy la: https://egarizim.com/chants-desperance/CE-CR/54-leve-nanm-mwen-ak-soley-la
- [CE-FR - Chants d'Espérance (Français)] N° 35 - Par tous les saints glorifiés: https://egarizim.com/chants-desperance/CE-FR/35-par-tous-les-saints-glorifies
- [CE-CR - Chants d'Espérance (Kreyòl)] N° 74 - A la yon Zanmi se Jezi: https://egarizim.com/chants-desperance/CE-CR/74-a-la-yon-zanmi-se-jezi`;

export const ImportFavoritesModal: React.FC<ImportFavoritesModalProps> = ({
  isOpen,
  onOpenChange,
  onImportSuccess,
}) => {
  const textareaId = useId();
  const [rawText, setRawText] = useState<string>("");
  const [parsedItems, setParsedItems] = useState<ParsedFavoriteHymn[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [invalidLines, setInvalidLines] = useState<string[]>([]);
  const [duplicateCount, setDuplicateCount] = useState<number>(0);
  const [importMode, setImportMode] = useState<"merge" | "replace">("merge");
  const [isEnriching, setIsEnriching] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showInvalidDetails, setShowInvalidDetails] = useState<boolean>(false);

  // Parse text live whenever rawText changes
  useEffect(() => {
    if (!rawText.trim()) {
      setParsedItems([]);
      setSelectedKeys(new Set());
      setInvalidLines([]);
      setDuplicateCount(0);
      return;
    }

    const result = parseFavoritesText(rawText);
    setParsedItems(result.validItems);
    setInvalidLines(result.invalidLines);
    setDuplicateCount(result.duplicateCount);

    // Select all valid items by default
    const keys = new Set<string>();
    result.validItems.forEach((item) => {
      keys.add(`${item.section_code}-${item.number}`);
    });
    setSelectedKeys(keys);

    // Asynchronously enrich audio & official metadata
    if (result.validItems.length > 0) {
      let isCurrent = true;
      setIsEnriching(true);
      enrichFavoritesWithApi(result.validItems)
        .then((enriched) => {
          if (isCurrent) {
            setParsedItems(enriched);
          }
        })
        .finally(() => {
          if (isCurrent) {
            setIsEnriching(false);
          }
        });
      return () => {
        isCurrent = false;
      };
    }
  }, [rawText]);

  // Reset states when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setRawText("");
      setParsedItems([]);
      setSelectedKeys(new Set());
      setInvalidLines([]);
      setDuplicateCount(0);
      setIsEnriching(false);
      setIsSubmitting(false);
      setShowInvalidDetails(false);
    }
  }, [isOpen]);

  const handlePasteClipboard = async () => {
    try {
      if (navigator?.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        if (text) {
          setRawText(text);
          toast.success("Texte collé depuis le presse-papiers !");
          return;
        }
      }
      toast.info("Veuillez coller le texte directement dans la zone de saisie");
    } catch {
      toast.info("Veuillez coller le texte manuellement (Ctrl+V)");
    }
  };

  const handleLoadSample = () => {
    setRawText(SAMPLE_EXPORT_TEXT);
    toast.success("Exemple de favoris chargé !");
  };

  const handleClearText = () => {
    setRawText("");
    setParsedItems([]);
    setSelectedKeys(new Set());
    setInvalidLines([]);
  };

  const toggleItemSelection = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedKeys.size === parsedItems.length) {
      setSelectedKeys(new Set());
    } else {
      const all = new Set<string>();
      parsedItems.forEach((item) => all.add(`${item.section_code}-${item.number}`));
      setSelectedKeys(all);
    }
  };

  const handleExecuteImport = () => {
    const itemsToImport = parsedItems.filter((item) =>
      selectedKeys.has(`${item.section_code}-${item.number}`)
    );

    if (itemsToImport.length === 0) {
      toast.error("Veuillez sélectionner au moins un cantique à importer.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = applyImportFavorites(itemsToImport, importMode);
      if (result.success) {
        toast.success(
          `${result.count} cantique${result.count > 1 ? "s" : ""} importé${
            result.count > 1 ? "s" : ""
          } dans vos favoris !`
        );
        if (onImportSuccess) {
          onImportSuccess();
        }
        onOpenChange(false);
      } else {
        toast.error("Impossible d'importer les favoris");
      }
    } catch (err) {
      console.error("Failed to import favorites:", err);
      toast.error("Erreur lors de l'import des favoris");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCount = selectedKeys.size;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg sm:max-w-xl bg-slate-900 border border-slate-800 text-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl shadow-2xl backdrop-blur-xl max-h-[90vh] overflow-y-auto scrollbar-tiny">
        {/* Header */}
        <DialogHeader className="text-left space-y-1.5 pr-6">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400 flex-shrink-0">
              <FolderInput className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-base sm:text-lg font-bold text-white tracking-tight truncate">
                Importer des favoris
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-slate-400 leading-tight">
                Collez une liste de liens ou de cantiques exportés pour les
                ajouter à vos favoris.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Text Input Area */}
        <div className="space-y-2 mt-2">
          <div className="flex items-center justify-between gap-2 text-xs">
            <label
              htmlFor={textareaId}
              className="text-slate-300 font-semibold flex items-center gap-1.5"
            >
              <span>Texte ou liens exportés</span>
            </label>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handlePasteClipboard}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-sky-400 hover:text-sky-300 text-[11px] font-semibold transition-all border border-slate-700/80 cursor-pointer"
                title="Coller depuis le presse-papiers"
              >
                <ClipboardPaste className="w-3 h-3" />
                <span>Coller</span>
              </button>
              <button
                type="button"
                onClick={handleLoadSample}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-slate-200 text-[11px] font-semibold transition-all border border-slate-700/60 cursor-pointer"
                title="Charger un exemple pour tester"
              >
                <HelpCircle className="w-3 h-3 text-amber-400" />
                <span>Exemple</span>
              </button>
              {rawText && (
                <button
                  type="button"
                  onClick={handleClearText}
                  className="p-1 rounded-lg bg-slate-800/80 hover:bg-rose-950/40 text-slate-400 hover:text-rose-300 transition-all border border-slate-700/60 cursor-pointer"
                  title="Effacer le texte"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <textarea
            id={textareaId}
            rows={4}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder={`Exemple :\n- [CE-CR - Chants d'Espérance] N° 54 - Leve, nanm mwen: https://egarizim.com/chants-desperance/CE-CR/54-leve-nanm-mwen\n- https://egarizim.com/chants-desperance/CE-FR/35`}
            className="w-full rounded-xl bg-slate-950 border border-slate-800 p-3 text-xs sm:text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 font-mono resize-y leading-relaxed"
          />
        </div>

        {/* Live Parsing Stats & Warnings */}
        {rawText.trim() && (
          <div className="space-y-3 pt-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-sky-500/15 border border-sky-500/30 text-sky-300 text-xs font-bold">
                  <Music className="w-3 h-3" />
                  <span>
                    {parsedItems.length} chant{parsedItems.length > 1 ? "s" : ""}{" "}
                    détecté{parsedItems.length > 1 ? "s" : ""}
                  </span>
                </span>

                {isEnriching && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 animate-pulse">
                    <Loader2 className="w-3 h-3 animate-spin text-sky-400" />
                    <span>Synchronisation audio...</span>
                  </span>
                )}

                {duplicateCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-[10px]">
                    {duplicateCount} doublon{duplicateCount > 1 ? "s" : ""} ignoré
                    {duplicateCount > 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {parsedItems.length > 0 && (
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-[11px] text-sky-400 hover:text-sky-300 font-medium underline cursor-pointer"
                >
                  {selectedKeys.size === parsedItems.length
                    ? "Tout désélectionner"
                    : "Tout sélectionner"}
                </button>
              )}
            </div>


            {/* Invalid lines warning collapsible */}
            {invalidLines.length > 0 && (
              <div className="rounded-xl bg-slate-950/80 border border-slate-800 text-xs">
                <button
                  type="button"
                  onClick={() => setShowInvalidDetails((prev) => !prev)}
                  className="w-full flex items-center justify-between p-2.5 text-slate-400 hover:text-slate-200 cursor-pointer"
                >
                  <span className="flex items-center gap-1.5 text-rose-400 font-medium">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>
                      {invalidLines.length} ligne
                      {invalidLines.length > 1 ? "s" : ""} non reconnue
                      {invalidLines.length > 1 ? "s" : ""}
                    </span>
                  </span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                      showInvalidDetails ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {showInvalidDetails && (
                  <div className="p-2.5 pt-0 border-t border-slate-800 text-[10px] font-mono text-slate-500 space-y-1 max-h-24 overflow-y-auto">
                    {invalidLines.map((line, idx) => (
                      <div key={idx} className="truncate">
                        • {line}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preview List of Parsed Items */}
            {parsedItems.length > 0 ? (
              <div className="max-h-48 sm:max-h-56 overflow-y-auto space-y-1.5 p-1 rounded-xl bg-slate-950/60 border border-slate-800/80 scrollbar-tiny">
                {parsedItems.map((item) => {
                  const key = `${item.section_code}-${item.number}`;
                  const isSelected = selectedKeys.has(key);

                  return (
                    <div
                      key={key}
                      onClick={() => toggleItemSelection(key)}
                      className={`flex items-center justify-between gap-2 p-2 rounded-lg border transition-all cursor-pointer ${
                        isSelected
                          ? "bg-slate-900 border-sky-500/50 text-white"
                          : "bg-slate-950/60 border-slate-800/70 text-slate-400 opacity-60 hover:opacity-100"
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border text-[10px] transition-colors flex-shrink-0 ${
                            isSelected
                              ? "bg-sky-500 border-sky-400 text-slate-950 font-bold"
                              : "border-slate-700 bg-slate-800 text-transparent"
                          }`}
                        >
                          <Check className="w-3 h-3 stroke-[3]" />
                        </div>

                        <span className="px-1.5 py-0.5 rounded bg-sky-950/80 border border-sky-500/30 text-sky-300 font-mono text-[10px] font-bold flex-shrink-0">
                          {item.section_code} N° {item.number}
                        </span>

                        <span className="text-xs font-medium truncate">
                          {item.title}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {item.media_url && (
                          <span
                            className="p-1 rounded bg-emerald-500/10 text-emerald-400"
                            title="Audio disponible"
                          >
                            <Volume2 className="w-3 h-3" />
                          </span>
                        )}

                        {item.isAlreadyFavorited ? (
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[9px] font-bold">
                            Déjà favori
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-bold">
                            Nouveau
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center rounded-xl bg-slate-950/60 border border-dashed border-slate-800 text-slate-400 text-xs">
                Aucun cantique reconnu dans le texte collé. Vérifiez le format.
              </div>
            )}

            {/* Import Mode Options */}
            {parsedItems.length > 0 && (
              <div className="space-y-1.5 pt-1">
                <label className="text-xs font-semibold text-slate-300">
                  Mode d'importation
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setImportMode("merge")}
                    className={`p-2 sm:p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                      importMode === "merge"
                        ? "bg-sky-950/40 border-sky-500/60 text-sky-200 ring-1 ring-sky-500/30"
                        : "bg-slate-950/60 hover:bg-slate-800/80 border-slate-800 text-slate-400"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Plus className="w-3.5 h-3.5 text-sky-400" />
                      <span className="text-xs font-bold text-slate-100">
                        Ajouter
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 line-clamp-1">
                      Conserver vos favoris actuels
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setImportMode("replace")}
                    className={`p-2 sm:p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                      importMode === "replace"
                        ? "bg-amber-950/40 border-amber-500/60 text-amber-200 ring-1 ring-amber-500/30"
                        : "bg-slate-950/60 hover:bg-slate-800/80 border-slate-800 text-slate-400"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <RefreshCw className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-xs font-bold text-slate-100">
                        Remplacer
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 line-clamp-1">
                      Écraser la liste actuelle
                    </p>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal Actions */}
        <div className="flex items-center justify-end gap-2.5 pt-3 mt-2 border-t border-slate-800/80">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3.5 py-2 rounded-xl bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold border border-slate-700/80 transition-all cursor-pointer"
          >
            Annuler
          </button>

          <button
            type="button"
            disabled={
              selectedCount === 0 || isSubmitting || parsedItems.length === 0
            }
            onClick={handleExecuteImport}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-bold text-xs shadow-lg shadow-sky-500/20 transition-all cursor-pointer active:scale-98"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Importation...</span>
              </>
            ) : (
              <>
                <FileDown className="w-3.5 h-3.5" />
                <span>
                  Importer{" "}
                  {selectedCount > 0
                    ? `${selectedCount} cantique${
                        selectedCount > 1 ? "s" : ""
                      }`
                    : ""}
                </span>
              </>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
